import { useIsDesktop } from '@/hooks/useBreakpoint';
import { ErrorState } from '@/components/ui/async-state';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { PulseAlertActionSheet } from '@/features/pulse/PulseAlertActionSheet';
import { PulseEmptyState } from '@/features/pulse/PulseEmptyState';
import { PulseLoadingState } from '@/features/pulse/PulseLoadingState';
import { PulseSwipeHintBanner } from '@/features/pulse/PulseSwipeHintBanner';
import { usePulseSwipeHint } from '@/features/pulse/usePulseSwipeHint';
import { resolveEventPosterURL } from '@/features/events/shared/posterURL';
import type {
  FriendActivityItem,
  NotificationItem,
  NotificationType,
  NotificationsResponse,
} from '@/lib/api/apiTypes';
import { useAuth } from '@/lib/auth/auth';
import { openNotificationTarget } from '@/lib/notifications/notifications';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { type ActivityItem } from '@/lib/api/apiTypes';
import { formatRelativeTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getEventHref, getProfileHref } from '@/lib/navigation/routes';
import { showToast } from '@/lib/useToastStore';
import { useTabSwipeGuardStore } from '@/lib/useTabSwipeGuardStore';
import { useActivity, useDismissActivity } from '@/features/pulse/useActivity';
import {
  getPulseAlertSections,
  useFriendActivity,
  notificationKeys,
  useDeleteNotification,
  useMarkNotificationEventNotInterested,
  useMarkNotificationRead,
  useNotifications,
} from '@/features/pulse/useNotifications';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { Image } from 'expo-image';
import { router, useFocusEffect } from 'expo-router';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import {
  BadgeCheckIcon,
  BellRingIcon,
  BookmarkIcon,
  CalendarPlusIcon,
  CalendarRangeIcon,
  CalendarXIcon,
  MoreHorizontalIcon,
  MegaphoneIcon,
  MessageCircleMoreIcon,
  SparklesIcon,
  TicketPercentIcon,
  Trash2Icon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  InteractionManager,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native';
import Reanimated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type ActivityTone = {
  Icon: LucideIcon;
  label: string;
  iconWrapClassName: string;
  iconClassName: string;
};

type NotificationTone = {
  Icon: LucideIcon;
  label: string;
  badgeClassName: string;
  iconColor: string;
  iconBackgroundColor: string;
};

type NotificationSettingsCategory = 'social' | 'reminders' | 'host_updates' | 'weekly_digest';

type ActivitySection = {
  key: 'comment' | 'rsvp' | 'host';
  title: string;
  data: ActivityItem[];
};

const ACTIVITY_TONES: Record<ActivitySection['key'], ActivityTone> = {
  comment: {
    Icon: MessageCircleMoreIcon,
    label: 'Comment',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
  rsvp: {
    Icon: UsersIcon,
    label: 'Going',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
  host: {
    Icon: CalendarRangeIcon,
    label: 'Host',
    iconWrapClassName: 'border border-border bg-surface-alt/70',
    iconClassName: 'text-foreground/80',
  },
};

const NOTIFICATION_TONES: Record<string, NotificationTone> = {
  friend_rsvp: {
    Icon: UsersIcon,
    label: 'Going',
    badgeClassName: 'border-primary/20 bg-primary/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
  follow_added: {
    Icon: UserPlusIcon,
    label: 'Follow',
    badgeClassName: 'border-status-live/20 bg-status-live/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
  going_event_starting_soon: {
    Icon: BellRingIcon,
    label: 'Going',
    badgeClassName: 'border-status-soon/20 bg-status-soon/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
  saved_event_starting_soon: {
    Icon: BookmarkIcon,
    label: 'Reminder',
    badgeClassName: 'border-status-soon/20 bg-status-soon/10',
    iconColor: DESIGN_TOKENS.colors.textMuted,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.textMuted, 0.12),
  },
  weekly_digest: {
    Icon: SparklesIcon,
    label: 'Weekend',
    badgeClassName: 'border-primary/20 bg-primary/10',
    iconColor: DESIGN_TOKENS.colors.textMuted,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.textMuted, 0.12),
  },
  event_boost: {
    Icon: ZapIcon,
    label: 'Boost',
    badgeClassName: 'border-status-soon/20 bg-status-soon/10',
    iconColor: '#fbbf24',
    iconBackgroundColor: withAlpha('#fbbf24', 0.12),
  },
  event_cancelled: {
    Icon: XCircleIcon,
    label: 'Canceled',
    badgeClassName: 'border-destructive/20 bg-destructive/10',
    iconColor: '#f87171',
    iconBackgroundColor: withAlpha('#f87171', 0.12),
  },
  venue_claim_approved: {
    Icon: BadgeCheckIcon,
    label: 'Live',
    badgeClassName: 'border-status-live/20 bg-status-live/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
  venue_new_event: {
    Icon: CalendarPlusIcon,
    label: 'Venue',
    badgeClassName: 'border-primary/20 bg-primary/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
  venue_event_cancelled: {
    Icon: CalendarXIcon,
    label: 'Canceled',
    badgeClassName: 'border-destructive/20 bg-destructive/10',
    iconColor: '#f87171',
    iconBackgroundColor: withAlpha('#f87171', 0.12),
  },
  venue_live_deal: {
    Icon: TicketPercentIcon,
    label: 'Deal',
    badgeClassName: 'border-status-soon/20 bg-status-soon/10',
    iconColor: '#fbbf24',
    iconBackgroundColor: withAlpha('#fbbf24', 0.12),
  },
  venue_announcement: {
    Icon: MegaphoneIcon,
    label: 'Venue',
    badgeClassName: 'border-primary/20 bg-primary/10',
    iconColor: DESIGN_TOKENS.colors.accent,
    iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.accent, 0.12),
  },
};

const DEFAULT_NOTIFICATION_TONE: NotificationTone = {
  Icon: BellRingIcon,
  label: 'Alert',
  badgeClassName: 'border-border bg-surface-alt/70',
  iconColor: DESIGN_TOKENS.colors.textMuted,
  iconBackgroundColor: withAlpha(DESIGN_TOKENS.colors.textMuted, 0.12),
};

const SWIPE_ACTION_WIDTH = 112;
const SWIPE_NOTIFICATION_ACTION_WIDTH = 132;
const SWIPE_CLOSE_DISTANCE = 18;
// Web needs gentler thresholds so row actions do not fight trackpad and wheel scrolling.
const SWIPE_ACTIVE_OFFSET = Platform.OS === 'web' ? 7 : 12;
const SWIPE_VERTICAL_FAIL_OFFSET = Platform.OS === 'web' ? 24 : 10;
const SWIPE_DISMISS_DISTANCE_RATIO = Platform.OS === 'web' ? 0.32 : 0.6;
const SWIPE_DISMISS_MAX_DISTANCE = Platform.OS === 'web' ? 96 : 160;
const WEB_SWIPE_ROW_STYLE =
  Platform.OS === 'web'
    ? ({
        touchAction: 'pan-y',
        userSelect: 'none',
      } as any)
    : undefined;
const SWIPE_ACTION_CONTAINER_STYLE = {
  alignItems: 'center' as const,
  bottom: 0,
  elevation: 8,
  flexDirection: 'row' as const,
  justifyContent: 'flex-end' as const,
  position: 'absolute' as const,
  right: 20,
  top: 0,
  zIndex: 30,
};
const SWIPE_SPRING = {
  damping: 24,
  mass: 0.9,
  stiffness: 260,
};
const PULSE_TRANSITION_FRIEND_LIMIT = 4;
const PULSE_TRANSITION_ALERT_LIMIT = 5;
const PULSE_TRANSITION_ACTIVITY_SECTION_LIMIT = 5;

export default function PulseScreen() {
  const isDesktop = useIsDesktop();
  const insets = useSafeAreaInsets();
  const session = useAuth((state) => state.session);
  const activityQuery = useActivity();
  const dismissActivity = useDismissActivity();
  const friendActivityQuery = useFriendActivity();
  const notificationsQuery = useNotifications();
  const markRead = useMarkNotificationRead();
  const deleteNotification = useDeleteNotification();
  const markNotInterested = useMarkNotificationEventNotInterested();
  const setTabSwipeBlocked = useTabSwipeGuardStore((state) => state.setBlocked);
  const queryClient = useQueryClient();
  const alertOptionsSheetRef = React.useRef<BottomSheetModal>(null);
  const scrollViewRef = React.useRef<ScrollView>(null);
  const webScrollOffsetRef = React.useRef(0);
  const [headerHasSeparator, setHeaderHasSeparator] = React.useState(false);
  const [swipeResetKey, setSwipeResetKey] = React.useState(0);
  const [alertOptionsItem, setAlertOptionsItem] = React.useState<NotificationItem | null>(null);
  const [pulseRowsExpanded, setPulseRowsExpanded] = React.useState(true);
  const hasOpenedSwipeRowRef = React.useRef(false);
  const { markSeen: markPulseSwipeHintSeen, shouldShow: showPulseSwipeHint } =
    usePulseSwipeHint();

  const notifications = React.useMemo(
    () => notificationsQuery.data?.notifications ?? [],
    [notificationsQuery.data?.notifications]
  );
  const alertSections = React.useMemo(
    () => getPulseAlertSections(notifications),
    [notifications]
  );
  const friendActivityItems = React.useMemo(
    () => friendActivityQuery.data?.activity ?? [],
    [friendActivityQuery.data?.activity]
  );
  const passiveFriendActivityKeys = React.useMemo(
    () =>
      new Set(
        friendActivityItems
          .filter((item) => item.type === 'friend_rsvp' && item.event?.id)
          .map((item) => `${item.actor.user_id}:${item.event?.id}`)
      ),
    [friendActivityItems]
  );
  const friendAlertItems = React.useMemo(
    () =>
      notifications.filter((item) => {
        if (item.type !== 'friend_rsvp') {
          return false;
        }
        const actorID = item.payload.subject_user_id;
        const eventID = item.payload.event_id ?? item.event_id;
        return !actorID || !eventID || !passiveFriendActivityKeys.has(`${actorID}:${eventID}`);
      }),
    [notifications, passiveFriendActivityKeys]
  );
  const alertCount = React.useMemo(
    () => alertSections.reduce((sum, section) => sum + section.data.length, 0),
    [alertSections]
  );
  const activityItems = React.useMemo(
    () =>
      (activityQuery.data?.activity ?? []).filter(
        (item) => item.type !== 'follow' && item.type !== 'reminder'
      ),
    [activityQuery.data?.activity]
  );
  const activitySections = React.useMemo(() => buildActivitySections(activityItems), [activityItems]);
  const pulseRowCount =
    friendActivityItems.length + friendAlertItems.length + alertCount + activityItems.length;
  const shouldStagePulseRows = !isDesktop && pulseRowCount > 16;
  const visibleFriendActivityItems = React.useMemo(
    () =>
      pulseRowsExpanded || !shouldStagePulseRows
        ? friendActivityItems
        : friendActivityItems.slice(0, PULSE_TRANSITION_FRIEND_LIMIT),
    [friendActivityItems, pulseRowsExpanded, shouldStagePulseRows]
  );
  const visibleFriendAlertItems = React.useMemo(
    () =>
      pulseRowsExpanded || !shouldStagePulseRows
        ? friendAlertItems
        : friendAlertItems.slice(0, PULSE_TRANSITION_ALERT_LIMIT),
    [friendAlertItems, pulseRowsExpanded, shouldStagePulseRows]
  );
  const visibleAlertSections = React.useMemo(
    () =>
      pulseRowsExpanded || !shouldStagePulseRows
        ? alertSections
        : alertSections
            .map((section) => ({
              ...section,
              data: section.data.slice(0, PULSE_TRANSITION_ALERT_LIMIT),
            }))
            .filter((section) => section.data.length > 0),
    [alertSections, pulseRowsExpanded, shouldStagePulseRows]
  );
  const visibleActivitySections = React.useMemo(
    () =>
      pulseRowsExpanded || !shouldStagePulseRows
        ? activitySections
        : activitySections
            .map((section) => ({
              ...section,
              data: section.data.slice(0, PULSE_TRANSITION_ACTIVITY_SECTION_LIMIT),
            }))
            .filter((section) => section.data.length > 0),
    [activitySections, pulseRowsExpanded, shouldStagePulseRows]
  );
  const hasPulseContent =
    friendActivityItems.length > 0 ||
    friendAlertItems.length > 0 ||
    alertSections.length > 0 ||
    activitySections.length > 0;
  const hasSwipeablePulseRows = alertCount > 0 || friendAlertItems.length > 0 || activityItems.length > 0;
  const isLoading = Boolean(session) && (activityQuery.isPending || notificationsQuery.isPending);
  const isRefreshing =
    Boolean(session) &&
    (activityQuery.isRefetching || friendActivityQuery.isRefetching || notificationsQuery.isRefetching) &&
    !isLoading;
  const error = session ? getPulseError(notificationsQuery.error, activityQuery.error) : null;
  const friendlyPulseError = React.useMemo(() => toFriendlyPulseError(error), [error]);

  const handleOpenAlert = React.useCallback(
    (item: NotificationItem) => {
      if (!item.is_read) {
        queryClient.setQueryData<NotificationsResponse>(notificationKeys.current(), (current) =>
          current
            ? {
                ...current,
                notifications: current.notifications.map((entry) =>
                  entry.id === item.id ? { ...entry, is_read: true } : entry
                ),
              }
            : current
        );
        queryClient.setQueriesData<NotificationsResponse>({ queryKey: notificationKeys.all }, (current) =>
          current
            ? {
                ...current,
                notifications: current.notifications.map((entry) =>
                  entry.id === item.id ? { ...entry, is_read: true } : entry
                ),
              }
            : current
        );
        markRead.mutate(item.id);
      }
      openNotificationTarget(item.payload);
    },
    [markRead, queryClient]
  );

  const handleDismissActivity = React.useCallback(
    (activityID: string) => {
      dismissActivity.mutate(activityID);
    },
    [dismissActivity]
  );

  const handleDeleteAlert = React.useCallback(
    (notificationID: string) => {
      deleteNotification.mutate(notificationID);
    },
    [deleteNotification]
  );

  const handleNotForMeAlert = React.useCallback(
    (item: NotificationItem) => {
      if (!item.event_id) {
        return;
      }

      setAlertOptionsItem(null);
      markNotInterested.mutate(item.event_id, {
        onSuccess: () => {
          deleteNotification.mutate(item.id);
        },
        onError: () => {
          showToast({ message: 'Unable to update this recommendation right now.' });
        },
      });
    },
    [deleteNotification, markNotInterested]
  );

  const handleMuteAlertType = React.useCallback((item: NotificationItem) => {
    const category = getNotificationSettingsCategory(item.type);
    if (!category) {
      return;
    }

    setAlertOptionsItem(null);
    router.push({
      pathname: '/settings-notifications',
      params: { category },
    });
  }, []);

  const handleOpenAlertOptions = React.useCallback((item: NotificationItem) => {
    setAlertOptionsItem(item);
  }, []);

  const selectedAlertOptionsCategory = alertOptionsItem
    ? getNotificationSettingsCategory(alertOptionsItem.type)
    : null;

  const handleCloseAlertOptions = React.useCallback(() => {
    setAlertOptionsItem(null);
  }, []);

  React.useEffect(() => {
    if (!alertOptionsItem) {
      alertOptionsSheetRef.current?.dismiss();
      return;
    }

    const presentFrame = requestAnimationFrame(() => {
      alertOptionsSheetRef.current?.present();
    });

    return () => cancelAnimationFrame(presentFrame);
  }, [alertOptionsItem]);

  const refreshInbox = React.useCallback(async () => {
    await Promise.allSettled([
      notificationsQuery.refetch(),
      activityQuery.refetch(),
      friendActivityQuery.refetch(),
    ]);
  }, [activityQuery, friendActivityQuery, notificationsQuery]);

  const handleScroll = React.useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const nextOffset = event.nativeEvent.contentOffset.y;
      if (Platform.OS === 'web') {
        webScrollOffsetRef.current = nextOffset;
      }
      setHeaderHasSeparator((current) => {
        const next = nextOffset > 10;
        return current === next ? current : next;
      });
    },
    []
  );

  const handlePulseSwipeOpen = React.useCallback(() => {
    hasOpenedSwipeRowRef.current = true;
    markPulseSwipeHintSeen();
  }, [markPulseSwipeHintSeen]);

  useFocusEffect(
    React.useCallback(() => {
      if (shouldStagePulseRows) {
        setPulseRowsExpanded(false);
      } else {
        setPulseRowsExpanded(true);
      }
      const expandTask = InteractionManager.runAfterInteractions(() => {
        setPulseRowsExpanded(true);
      });

      return () => {
        expandTask.cancel();
        if (shouldStagePulseRows) {
          setPulseRowsExpanded(false);
        }
        if (hasOpenedSwipeRowRef.current) {
          hasOpenedSwipeRowRef.current = false;
          setSwipeResetKey((current) => current + 1);
        }
        setTabSwipeBlocked(false);
      };
    }, [setTabSwipeBlocked, shouldStagePulseRows])
  );

  return (
    <>
      <AppScreen edges={[]}>
        {!isDesktop ? <PulseHeader hasSeparator={headerHasSeparator} topInset={insets.top} /> : null}
        <ScrollView
          ref={scrollViewRef}
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingBottom: isDesktop ? 40 : 112,
          }}
          refreshControl={
            session ? (
              <RefreshControl
                refreshing={isRefreshing}
                onRefresh={() => refreshInbox().catch(() => undefined)}
              />
            ) : undefined
          }
          onScroll={handleScroll}
          scrollEventThrottle={16}>
          <View
            style={
              isDesktop
                ? {
                    width: '100%',
                    maxWidth: 760,
                    alignSelf: 'center',
                    paddingHorizontal: 24,
                    paddingTop: 28,
                  }
                : undefined
            }>
            {isDesktop ? <DesktopPulseHeader alertCount={alertCount} /> : null}
            {!session ? <View className="px-5 pb-28"><SignInPromptBlock /></View> : null}

            {session && isLoading ? <PulseLoadingState /> : null}

            {session && !isLoading && !error ? (
              <View className="gap-7 pb-28">
                {!isDesktop && showPulseSwipeHint && hasSwipeablePulseRows ? (
                  <PulseSwipeHintBanner onDismiss={markPulseSwipeHintSeen} />
                ) : null}

                {friendActivityQuery.isPending ||
                friendActivityItems.length > 0 ||
                friendAlertItems.length > 0 ? (
                  <FriendActivitySection
                    deletingID={
                      deleteNotification.isPending ? (deleteNotification.variables ?? null) : null
                    }
                    emphasized={alertCount === 0 && friendAlertItems.length === 1}
                    isDesktop={isDesktop}
                    items={visibleFriendActivityItems}
                    isLoading={friendActivityQuery.isPending}
                    notificationItems={visibleFriendAlertItems}
                    onDelete={handleDeleteAlert}
                    onMarkNotForMe={handleNotForMeAlert}
                    onMore={handleOpenAlertOptions}
                    onMute={handleMuteAlertType}
                    onOpen={handleOpenAlert}
                    onSwipeOpen={handlePulseSwipeOpen}
                    setTabSwipeBlocked={setTabSwipeBlocked}
                    swipeResetKey={swipeResetKey}
                  />
                ) : null}

                {visibleAlertSections.length > 0 ? (
                  <InboxBlock title="" prominent>
                    {visibleAlertSections.map((section) => (
                      <AlertBucket
                        key={section.key}
                        title={section.title}
                        showTitle={visibleAlertSections.length > 1}
                        emphasized={alertCount === 1}
                        items={section.data}
                        isDesktop={isDesktop}
                        deletingID={
                          deleteNotification.isPending ? (deleteNotification.variables ?? null) : null
                        }
                        onDelete={handleDeleteAlert}
                        onMarkNotForMe={handleNotForMeAlert}
                        onMore={handleOpenAlertOptions}
                        onMute={handleMuteAlertType}
                        onOpen={handleOpenAlert}
                        onSwipeOpen={handlePulseSwipeOpen}
                        swipeResetKey={swipeResetKey}
                        setTabSwipeBlocked={setTabSwipeBlocked}
                      />
                    ))}
                  </InboxBlock>
                ) : null}

                {visibleActivitySections.length > 0 ? (
                  <View className="gap-5">
                    {visibleActivitySections.map((section) => (
                      <ActivityBucket
                        key={section.key}
                        dismissingID={
                          dismissActivity.isPending ? (dismissActivity.variables ?? null) : null
                        }
                        section={section}
                        isDesktop={isDesktop}
                        onDismiss={handleDismissActivity}
                        onSwipeOpen={handlePulseSwipeOpen}
                        setTabSwipeBlocked={setTabSwipeBlocked}
                        swipeResetKey={swipeResetKey}
                      />
                    ))}
                  </View>
                ) : null}

                {!hasPulseContent ? <PulseEmptyState /> : null}
              </View>
            ) : null}

            {session && error ? (
              <ErrorState
                title={friendlyPulseError.title}
                body={friendlyPulseError.body}
                actionLabel="Retry"
                onAction={() => refreshInbox().catch(() => undefined)}
              />
            ) : null}
          </View>
        </ScrollView>
      </AppScreen>
      <PulseAlertActionSheet
        bottomSheetRef={alertOptionsSheetRef}
        canMarkNotForMe={Boolean(alertOptionsItem?.event_id)}
        canMute={Boolean(selectedAlertOptionsCategory)}
        item={alertOptionsItem}
        onClose={handleCloseAlertOptions}
        onMarkNotForMe={handleNotForMeAlert}
        onMute={handleMuteAlertType}
      />
    </>
  );
}

function PulseHeader({ hasSeparator, topInset }: { hasSeparator: boolean; topInset: number }) {
  return (
    <View
      style={{
        alignItems: 'center',
        backgroundColor: DESIGN_TOKENS.colors.canvas,
        borderBottomColor: hasSeparator ? DESIGN_TOKENS.colors.borderStrong : 'transparent',
        borderBottomWidth: 0.5,
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingBottom: 12,
        paddingHorizontal: 16,
        paddingTop: topInset + 8,
      }}>
      <View className="min-w-0 flex-1">
        <Text className="text-3xl font-extrabold tracking-tight">Pulse</Text>
      </View>

      <Button
        accessibilityLabel="Open notification settings"
        variant="outline"
        size="icon"
        onPress={() => router.push('/settings-notifications')}>
        <Icon as={BellRingIcon} size={18} className="text-foreground" />
      </Button>
    </View>
  );
}

function DesktopPulseHeader({ alertCount }: { alertCount: number }) {
  return (
    <View className="mb-7 flex-row items-end justify-between gap-4">
      <View className="min-w-0 flex-1 gap-1">
        <Text className="text-[32px] font-semibold tracking-tight text-foreground">Pulse</Text>
        <Text className="text-[13px] leading-5 text-muted-foreground/70">
          Useful alerts, friend activity, and venue updates in one calm feed.
        </Text>
      </View>
      <View className="flex-row items-center gap-2">
        {alertCount > 0 ? (
          <Badge variant="outline" className="border-primary/20 bg-primary/10 px-3 py-1">
            <Text variant="small" className="text-primary">
              {alertCount} new
            </Text>
          </Badge>
        ) : null}
        <Button
          accessibilityLabel="Open notification settings"
          variant="outline"
          size="icon"
          onPress={() => router.push('/settings-notifications')}>
          <Icon as={BellRingIcon} size={18} className="text-foreground" />
        </Button>
      </View>
    </View>
  );
}

function SignInPromptBlock() {
  return (
    <View className="gap-8 pt-4">
      {/* Ghost rows — fade out to show what's waiting behind sign-in */}
      <View>
        <View className="opacity-50">
          <GhostPulseRow hasImage lineWidths={['w-3/4', 'w-2/5']} />
        </View>
        <View className="h-px bg-border/50" />
        <View className="opacity-[0.28]">
          <GhostPulseRow hasImage={false} lineWidths={['w-1/2', 'w-1/3']} />
        </View>
        <View className="h-px bg-border/50" />
        <View className="opacity-[0.12]">
          <GhostPulseRow hasImage lineWidths={['w-2/3', 'w-2/5']} />
        </View>
      </View>

      {/* CTA */}
      <View className="items-center gap-5">
        <View className="items-center gap-2">
          <Text className="text-center text-[21px] font-bold tracking-tight text-foreground">
            Your feed is quiet.
          </Text>
          <Text className="text-center text-[15px] leading-6 text-muted-foreground">
            Sign in to see alerts, friend plans,{'\n'}and updates from venues you follow.
          </Text>
        </View>

        <Button
          className="min-h-12 w-full rounded-md"
          onPress={() => router.push('/sign-in')}>
          <Text className="font-semibold text-primary-foreground">Sign in</Text>
        </Button>
      </View>
    </View>
  );
}

function GhostPulseRow({
  hasImage,
  lineWidths,
}: {
  hasImage: boolean;
  lineWidths: [string, string];
}) {
  return (
    <View className="flex-row items-center gap-3 py-3.5">
      <View className="size-8 rounded-2xl bg-muted" />
      <View className="min-w-0 flex-1 gap-1.5">
        <View className={cn('h-3 rounded-full bg-muted', lineWidths[0])} />
        <View className={cn('h-2.5 rounded-full bg-muted/70', lineWidths[1])} />
      </View>
      {hasImage ? <View className="size-10 rounded-lg bg-muted/80" /> : null}
    </View>
  );
}

function InboxBlock({
  title,
  prominent = false,
  children,
}: {
  title: string;
  prominent?: boolean;
  children: React.ReactNode;
}) {
  const hasTitle = title.trim().length > 0;

  return (
    <View className={cn(prominent ? 'gap-1' : 'gap-3')}>
      {hasTitle ? (
        <View className="px-5">
          <Text
            className={cn(
              'uppercase',
              prominent
                ? 'text-[17px] font-semibold tracking-[1.5px] text-foreground'
                : 'text-[17px] font-semibold tracking-[1.5px] text-primary'
            )}>
            {title}
          </Text>
        </View>
      ) : null}
      <View className={cn(prominent ? 'gap-2' : 'gap-5')}>{children}</View>
    </View>
  );
}

function FriendActivitySection({
  deletingID,
  emphasized,
  isDesktop,
  isLoading,
  items,
  notificationItems,
  onDelete,
  onMarkNotForMe,
  onMore,
  onMute,
  onOpen,
  onSwipeOpen,
  setTabSwipeBlocked,
  swipeResetKey,
}: {
  deletingID: string | null;
  emphasized: boolean;
  isDesktop: boolean;
  isLoading: boolean;
  items: FriendActivityItem[];
  notificationItems: NotificationItem[];
  onDelete: (notificationID: string) => void;
  onMarkNotForMe: (item: NotificationItem) => void;
  onMore: (item: NotificationItem) => void;
  onMute: (item: NotificationItem) => void;
  onOpen: (item: NotificationItem) => void;
  onSwipeOpen: () => void;
  setTabSwipeBlocked: (blocked: boolean) => void;
  swipeResetKey: number;
}) {
  return (
    <View className="gap-1">
      <View className="px-5 pb-2">
        <View className="flex-row items-end gap-2">
          <Text variant="small" className="uppercase tracking-[1.5px] text-primary">
            Friends
          </Text>
          {items.length > 0 ? (
            <Text className="pb-0.5 text-[10px] text-muted-foreground/70">
              from mutual follows
            </Text>
          ) : null}
        </View>
      </View>

      {isLoading ? (
        <View className="gap-2 px-5">
          {Array.from({ length: 3 }).map((_, index) => (
            <View key={index} className="h-[72px] animate-pulse rounded-lg bg-muted/80" />
          ))}
        </View>
      ) : items.length > 0 || notificationItems.length > 0 ? (
        <View className="gap-1">
          {notificationItems.length > 0 ? (
            <AlertBucket
              title=""
              showTitle={false}
              emphasized={emphasized}
              items={notificationItems}
              isDesktop={isDesktop}
              deletingID={deletingID}
              onDelete={onDelete}
              onMarkNotForMe={onMarkNotForMe}
              onMore={onMore}
              onMute={onMute}
              onOpen={onOpen}
              onSwipeOpen={onSwipeOpen}
              setTabSwipeBlocked={setTabSwipeBlocked}
              swipeResetKey={swipeResetKey}
            />
          ) : null}
          {items.map((item) => (
            <FriendActivityRow key={friendActivityKey(item)} item={item} />
          ))}
        </View>
      ) : (
        <View className="px-5 py-7">
          <Text className="text-center text-[13px] leading-5 text-muted-foreground">
            Nothing from friends yet.{'\n'}Follow people you know to see their plans here.
          </Text>
        </View>
      )}
    </View>
  );
}

function FriendActivityRow({ item }: { item: FriendActivityItem }) {
  const actorName = item.actor.display_name || item.actor.username;
  const targetName = item.target_user?.display_name || item.target_user?.username || 'someone';
  const event = item.event ?? null;

  const handleOpenEvent = React.useCallback(() => {
    if (event?.id) {
      router.navigate(getEventHref(event.id) as never);
    }
  }, [event?.id]);

  const handleOpenActor = React.useCallback(() => {
    if (item.actor.username) {
      router.navigate(getProfileHref(item.actor.username));
    }
  }, [item.actor.username]);

  return (
    <View className="mx-5 flex-row items-center gap-3 border-b border-border/60 py-3">
      <Pressable
        accessibilityLabel={`Open ${actorName}'s profile`}
        accessibilityRole="button"
        onPress={handleOpenActor}>
        <Avatar alt={actorName} className="size-8 border border-border bg-surface-alt">
          {item.actor.avatar_url ? (
            <AvatarImage source={{ uri: item.actor.avatar_url }} className="h-full w-full" />
          ) : null}
          <AvatarFallback className="bg-transparent">
            <Text className="text-[11px] font-semibold leading-none text-foreground">
              {getProfileInitials(actorName)}
            </Text>
          </AvatarFallback>
        </Avatar>
      </Pressable>

      <Pressable
        accessibilityLabel={friendActivityAccessibilityLabel(item, actorName, targetName)}
        accessibilityRole={event ? 'button' : 'text'}
        className="min-w-0 flex-1"
        disabled={!event}
        onPress={handleOpenEvent}>
        <Text className="text-[13px] leading-5 text-muted-foreground" numberOfLines={2}>
          <Text className="font-medium text-foreground">{actorName}</Text>
          {friendActivityActionText(item, targetName)}
        </Text>
        <Text className="mt-0.5 text-[11px] leading-4 text-muted-foreground/75" numberOfLines={1}>
          {friendActivityMetaText(item)}
        </Text>
      </Pressable>

      {event?.poster_url ? (
        <Pressable
          accessibilityLabel={`Open ${event.title}`}
          accessibilityRole="button"
          onPress={handleOpenEvent}>
          <Image
            source={{ uri: resolveEventPosterURL(event.poster_url) }}
            contentFit="cover"
            className="size-10 rounded-lg bg-muted/80"
          />
        </Pressable>
      ) : null}
    </View>
  );
}

function AlertBucket({
  title,
  showTitle,
  emphasized,
  items,
  isDesktop,
  deletingID,
  onDelete,
  onMarkNotForMe,
  onMore,
  onMute,
  onOpen,
  onSwipeOpen,
  setTabSwipeBlocked,
  swipeResetKey,
}: {
  title: string;
  showTitle: boolean;
  emphasized: boolean;
  items: NotificationItem[];
  isDesktop: boolean;
  deletingID: string | null;
  onDelete: (notificationID: string) => void;
  onMarkNotForMe: (item: NotificationItem) => void;
  onMore: (item: NotificationItem) => void;
  onMute: (item: NotificationItem) => void;
  onOpen: (item: NotificationItem) => void;
  onSwipeOpen: () => void;
  setTabSwipeBlocked: (blocked: boolean) => void;
  swipeResetKey: number;
}) {
  return (
    <View className="gap-1">
      {showTitle ? (
        <View className="flex-row items-center justify-between px-5">
          <Text className="text-[18px] font-semibold tracking-tight text-foreground">{title}</Text>
          <Badge variant="outline" className="border-border bg-surface-alt/70 px-3 py-1">
            <Text variant="small" className="text-foreground">
              {items.length}
            </Text>
          </Badge>
        </View>
      ) : null}

      <View>
        {items.map((item) => (
          <View key={item.id}>
            {isDesktop ? (
              <DesktopNotificationRow
                item={item}
                emphasized={emphasized}
                isDeleting={deletingID === item.id}
                onDelete={() => onDelete(item.id)}
                onMarkNotForMe={() => onMarkNotForMe(item)}
                onMore={() => onMore(item)}
                onMute={() => onMute(item)}
                onOpen={() => onOpen(item)}
              />
            ) : (
              <PulseSwipeableNotificationRow
                item={item}
                emphasized={emphasized}
                onOpen={() => onOpen(item)}
                onDelete={() => onDelete(item.id)}
                onMore={() => onMore(item)}
                isDeleting={deletingID === item.id}
                onSwipeOpen={onSwipeOpen}
                setTabSwipeBlocked={setTabSwipeBlocked}
                swipeResetKey={swipeResetKey}
              />
            )}
            {item.id !== items[items.length - 1]?.id ? <View className="mx-5 h-px bg-border/70" /> : null}
          </View>
        ))}
      </View>
    </View>
  );
}

function ActivityBucket({
  dismissingID,
  isDesktop,
  onDismiss,
  onSwipeOpen,
  section,
  setTabSwipeBlocked,
  swipeResetKey,
}: {
  dismissingID: string | null;
  isDesktop: boolean;
  onDismiss: (activityID: string) => void;
  onSwipeOpen: () => void;
  section: ActivitySection;
  setTabSwipeBlocked: (blocked: boolean) => void;
  swipeResetKey: number;
}) {
  return (
    <View className="gap-1">
      <View className="flex-row items-center justify-between gap-3 px-5 pb-2">
        <Text variant="small" className="uppercase tracking-[1.5px] text-primary">
          {section.title}
        </Text>
        <Badge variant="outline" className="border-border bg-surface-alt/70 px-3 py-1">
          <Text variant="small" className="text-foreground">
            {section.data.length}
          </Text>
        </Badge>
      </View>

      <View>
        {section.data.map((item, index) => (
          <View key={item.id}>
            {isDesktop ? (
              <DesktopActivityRow
                item={item}
                isDismissing={dismissingID === item.id}
                onDismiss={() => onDismiss(item.id)}
              />
            ) : (
              <PulseSwipeableActivityRow
                item={item}
                isDismissing={dismissingID === item.id}
                onDismiss={() => onDismiss(item.id)}
                onSwipeOpen={onSwipeOpen}
                setTabSwipeBlocked={setTabSwipeBlocked}
                swipeResetKey={swipeResetKey}
              />
            )}
            {index < section.data.length - 1 ? <View className="mx-5 h-px bg-border/70" /> : null}
          </View>
        ))}
      </View>
    </View>
  );
}

function DesktopNotificationRow({
  emphasized,
  isDeleting,
  item,
  onDelete,
  onMarkNotForMe,
  onMore,
  onMute,
  onOpen,
}: {
  emphasized: boolean;
  isDeleting: boolean;
  item: NotificationItem;
  onDelete: () => void;
  onMarkNotForMe: () => void;
  onMore: () => void;
  onMute: () => void;
  onOpen: () => void;
}) {
  const tone = notificationTone(item.type);
  const headline = notificationHeadline(item);
  const supportingText = notificationSupportingText(item);
  const previewURL = notificationPreviewURL(item);

  return (
    <View className={cn('flex-row items-center gap-3 px-5 py-3.5', emphasized && 'mx-5 rounded-[18px] bg-primary/5')}>
      <Pressable
        accessibilityLabel={headline}
        accessibilityRole="button"
        className="min-w-0 flex-1 flex-row items-start gap-3"
        onPress={onOpen}>
        <NotificationIdentity item={item} tone={tone} />
        <View className="min-w-0 flex-1 gap-1">
          <View className="flex-row items-start gap-2">
            <Text className="min-w-0 flex-1 text-[14px] font-medium leading-5 text-foreground" numberOfLines={2}>
              {headline}
            </Text>
            <Text variant="small" className="shrink-0 text-[12px] text-muted-foreground/75">
              {formatCompactRelativeTime(item.created_at)}
            </Text>
          </View>
          {supportingText ? (
            <Text variant="muted" className="text-muted-foreground/65 leading-5" numberOfLines={2}>
              {supportingText}
            </Text>
          ) : null}
        </View>
        {previewURL ? (
          <Image
            source={{ uri: previewURL }}
            contentFit="cover"
            className="mt-0.5 size-10 rounded-lg bg-muted/80"
          />
        ) : null}
      </Pressable>
      {Platform.OS === 'web' ? (
        <DesktopPulseAlertActionsMenu
          canMarkNotForMe={Boolean(item.event_id)}
          canMute={Boolean(getNotificationSettingsCategory(item.type))}
          disabled={isDeleting}
          onDismiss={onDelete}
          onMarkNotForMe={onMarkNotForMe}
          onMute={onMute}
          onOpen={onOpen}
        />
      ) : (
        <View className="flex-row items-center gap-1">
          <DesktopPulseIconButton
            accessibilityLabel="More alert options"
            disabled={isDeleting}
            icon={MoreHorizontalIcon}
            onPress={onMore}
          />
          <DesktopPulseIconButton
            accessibilityLabel="Dismiss alert"
            destructive
            disabled={isDeleting}
            icon={Trash2Icon}
            onPress={onDelete}
          />
        </View>
      )}
    </View>
  );
}

function DesktopPulseAlertActionsMenu({
  canMarkNotForMe,
  canMute,
  disabled = false,
  onDismiss,
  onMarkNotForMe,
  onMute,
  onOpen,
}: {
  canMarkNotForMe: boolean;
  canMute: boolean;
  disabled?: boolean;
  onDismiss: () => void;
  onMarkNotForMe: () => void;
  onMute: () => void;
  onOpen: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          accessibilityLabel="Open alert actions"
          disabled={disabled}
          variant="ghost"
          size="icon"
          className="size-8 rounded-full border border-border/60 bg-surface-alt/60">
          <Icon as={MoreHorizontalIcon} size={15} className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-44">
        <DropdownMenuItem onPress={onOpen}>
          <Text>Open</Text>
        </DropdownMenuItem>
        {canMute ? (
          <DropdownMenuItem onPress={onMute}>
            <Text>Mute this type</Text>
          </DropdownMenuItem>
        ) : null}
        {canMarkNotForMe ? (
          <DropdownMenuItem onPress={onMarkNotForMe}>
            <Text>Not for me</Text>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onPress={onDismiss}>
          <Icon as={Trash2Icon} size={15} className="text-destructive" />
          <Text>Dismiss alert</Text>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopActivityRow({
  isDismissing,
  item,
  onDismiss,
}: {
  isDismissing: boolean;
  item: ActivityItem;
  onDismiss: () => void;
}) {
  const tone = activityTone(item.type);
  const actorHandle = item.actor_username ? `@${item.actor_username}` : null;

  function handlePress() {
    if (item.event_id) {
      router.navigate(getEventHref(item.event_id) as never);
      return;
    }

    if (item.actor_username) {
      router.navigate(getProfileHref(item.actor_username));
    }
  }

  return (
    <View className="flex-row items-center gap-3 px-5 py-3">
      <Pressable
        accessibilityLabel={item.title}
        accessibilityRole="button"
        className="min-w-0 flex-1 flex-row items-start gap-3"
        onPress={handlePress}>
        {actorHandle ? (
          <ActorAvatar label={actorHandle} className={activityAvatarClassName(item.type)} />
        ) : (
          <View
            className={cn(
              'mt-0.5 size-9 items-center justify-center rounded-2xl',
              tone.iconWrapClassName
            )}>
            <Icon as={tone.Icon} size={16} className={tone.iconClassName} />
          </View>
        )}
        <View className="min-w-0 flex-1 gap-1">
          <View className="flex-row items-start gap-2">
            <Text className="min-w-0 flex-1 text-[14px] font-medium leading-5 text-foreground" numberOfLines={2}>
              {item.title}
            </Text>
            <Text variant="small" className="shrink-0 text-[12px] text-muted-foreground/75">
              {formatCompactRelativeTime(item.occurred_at)}
            </Text>
          </View>
          {item.body ? (
            <Text variant="small" className="text-muted-foreground/65 leading-5" numberOfLines={2}>
              {item.body}
            </Text>
          ) : null}
        </View>
      </Pressable>
      {Platform.OS === 'web' ? (
        <DesktopPulseActionsMenu
          disabled={isDismissing}
          onOpen={handlePress}
          onDismiss={onDismiss}
          dismissLabel="Dismiss activity"
        />
      ) : (
        <DesktopPulseIconButton
          accessibilityLabel="Dismiss activity"
          destructive
          disabled={isDismissing}
          icon={Trash2Icon}
          onPress={onDismiss}
        />
      )}
    </View>
  );
}

function DesktopPulseActionsMenu({
  disabled = false,
  dismissLabel,
  onDismiss,
  onOpen,
}: {
  disabled?: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  onOpen: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          accessibilityLabel="Open row actions"
          disabled={disabled}
          variant="ghost"
          size="icon"
          className="size-8 rounded-full border border-border/60 bg-surface-alt/60">
          <Icon as={MoreHorizontalIcon} size={15} className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-44">
        <DropdownMenuItem onPress={onOpen}>
          <Text>Open</Text>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onPress={onDismiss}>
          <Icon as={Trash2Icon} size={15} className="text-destructive" />
          <Text>{dismissLabel}</Text>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopPulseIconButton({
  accessibilityLabel,
  destructive = false,
  disabled = false,
  icon,
  onPress,
}: {
  accessibilityLabel: string;
  destructive?: boolean;
  disabled?: boolean;
  icon: LucideIcon;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      disabled={disabled}
      className={cn(
        'size-8 items-center justify-center rounded-full border border-border/60 bg-surface-alt/60 active:opacity-80',
        disabled && 'opacity-50'
      )}
      onPress={onPress}>
      <Icon
        as={icon}
        size={15}
        className={destructive ? 'text-destructive' : 'text-muted-foreground'}
      />
    </Pressable>
  );
}

function PulseSwipeableActivityRow({
  item,
  isDismissing,
  onDismiss,
  onSwipeOpen,
  setTabSwipeBlocked,
  swipeResetKey,
}: {
  item: ActivityItem;
  isDismissing: boolean;
  onDismiss: () => void;
  onSwipeOpen: () => void;
  setTabSwipeBlocked: (blocked: boolean) => void;
  swipeResetKey: number;
}) {
  const translateX = useSharedValue(0);
  const rowWidth = useSharedValue(0);
  const gestureStartX = useSharedValue(0);
  const isOpenRef = React.useRef(false);
  const [isOpen, setIsOpen] = React.useState(false);

  const setSwipeOpen = React.useCallback(
    (isSwipeOpen: boolean) => {
      isOpenRef.current = isSwipeOpen;
      setIsOpen(isSwipeOpen);
      if (isSwipeOpen) {
        onSwipeOpen();
      }
    },
    [onSwipeOpen]
  );

  const handleDismiss = React.useCallback(() => {
    if (!isDismissing) {
      onDismiss();
    }
  }, [isDismissing, onDismiss]);

  const handleTouchRow = React.useCallback(() => {
    if (!isOpenRef.current) {
      return;
    }

    setSwipeOpen(false);
    translateX.value = withSpring(0, SWIPE_SPRING);
  }, [setSwipeOpen, translateX]);

  const panGesture = React.useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetX([-SWIPE_ACTIVE_OFFSET, SWIPE_ACTIVE_OFFSET])
        .failOffsetY([-SWIPE_VERTICAL_FAIL_OFFSET, SWIPE_VERTICAL_FAIL_OFFSET])
        .onBegin(() => {
          // Swipe rows temporarily own horizontal gestures so tab navigation does not steal the action.
          runOnJS(setTabSwipeBlocked)(true);
          gestureStartX.value = translateX.value;
        })
        .onUpdate((event) => {
          const width = rowWidth.value;
          const nextX = gestureStartX.value + event.translationX;
          translateX.value = Math.max(-width, Math.min(0, nextX));
        })
        .onEnd((event) => {
          const width = rowWidth.value;
          const distance = -translateX.value;
          const dismissDistance = Math.min(
            width * SWIPE_DISMISS_DISTANCE_RATIO,
            SWIPE_DISMISS_MAX_DISTANCE
          );

          if (
            width > 0 &&
            (distance >= dismissDistance ||
              (distance >= SWIPE_ACTION_WIDTH * 0.7 && event.velocityX < -450))
          ) {
            if (isDismissing) {
              runOnJS(setSwipeOpen)(false);
              translateX.value = withSpring(0, SWIPE_SPRING);
              return;
            }

            runOnJS(setSwipeOpen)(false);
            runOnJS(handleDismiss)();
            translateX.value = withSpring(-width, SWIPE_SPRING);
            return;
          }

          if (event.translationX > SWIPE_CLOSE_DISTANCE || event.velocityX > 250) {
            runOnJS(setSwipeOpen)(false);
            translateX.value = withSpring(0, SWIPE_SPRING);
            return;
          }

          if (distance >= SWIPE_ACTION_WIDTH * 0.34) {
            runOnJS(setSwipeOpen)(true);
            translateX.value = withSpring(-SWIPE_ACTION_WIDTH, SWIPE_SPRING);
            return;
          }

          runOnJS(setSwipeOpen)(false);
          translateX.value = withSpring(0, SWIPE_SPRING);
        })
        .onFinalize(() => {
          runOnJS(setTabSwipeBlocked)(false);
        }),
    [
      gestureStartX,
      handleDismiss,
      isDismissing,
      rowWidth,
      setSwipeOpen,
      setTabSwipeBlocked,
      translateX,
    ]
  );

  const closeTapGesture = React.useMemo(
    () =>
      Gesture.Tap().onEnd(() => {
        runOnJS(handleTouchRow)();
      }),
    [handleTouchRow]
  );

  const gesture = React.useMemo(
    () => Gesture.Simultaneous(panGesture, closeTapGesture),
    [closeTapGesture, panGesture]
  );

  const rowStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const highlightStyle = useAnimatedStyle(() => ({
    opacity: Math.min(0.7, Math.max(0, -translateX.value / SWIPE_ACTION_WIDTH)),
  }));

  const actionStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, Math.max(0, -translateX.value / SWIPE_ACTION_WIDTH)),
  }));

  const handlePressDismiss = React.useCallback(() => {
    if (isDismissing) {
      return;
    }

    setSwipeOpen(false);
    handleDismiss();
    translateX.value = withSpring(-rowWidth.value, SWIPE_SPRING);
  }, [handleDismiss, isDismissing, rowWidth, setSwipeOpen, translateX]);

  React.useEffect(() => {
    setSwipeOpen(false);
    translateX.value = withSpring(0, SWIPE_SPRING);
  }, [setSwipeOpen, swipeResetKey, translateX]);

  return (
    <View
      className="overflow-hidden"
      style={WEB_SWIPE_ROW_STYLE}
      onLayout={(event) => {
        rowWidth.value = event.nativeEvent.layout.width;
      }}>
      <Reanimated.View
        pointerEvents="none"
        className="absolute inset-0 bg-surface-alt/60"
        style={highlightStyle}
      />
      <GestureDetector gesture={gesture}>
        <Reanimated.View
          className="z-10"
          style={rowStyle}>
          <ActivityRow item={item} disabled={isOpen} />
        </Reanimated.View>
      </GestureDetector>
      <Reanimated.View
        pointerEvents="auto"
        style={[actionStyle, SWIPE_ACTION_CONTAINER_STYLE, { width: SWIPE_ACTION_WIDTH }]}>
        <PulseSwipeAction
          accessibilityLabel="Dismiss activity"
          accessibilityHint="Removes this activity item from Pulse for you"
          disabled={isDismissing}
          icon={Trash2Icon}
          iconClassName="text-destructive"
          className="bg-destructive/12"
          onPress={handlePressDismiss}
        />
      </Reanimated.View>
    </View>
  );
}

function PulseSwipeableNotificationRow({
  item,
  emphasized,
  onOpen,
  onDelete,
  onMore,
  onSwipeOpen,
  isDeleting,
  setTabSwipeBlocked,
  swipeResetKey,
}: {
  item: NotificationItem;
  emphasized: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onMore: () => void;
  onSwipeOpen: () => void;
  isDeleting: boolean;
  setTabSwipeBlocked: (blocked: boolean) => void;
  swipeResetKey: number;
}) {
  const translateX = useSharedValue(0);
  const rowWidth = useSharedValue(0);
  const gestureStartX = useSharedValue(0);
  const isOpenRef = React.useRef(false);
  const [isOpen, setIsOpen] = React.useState(false);

  const setSwipeOpen = React.useCallback(
    (isSwipeOpen: boolean) => {
      isOpenRef.current = isSwipeOpen;
      setIsOpen(isSwipeOpen);
      if (isSwipeOpen) {
        onSwipeOpen();
      }
    },
    [onSwipeOpen]
  );

  const handleDelete = React.useCallback(() => {
    if (!isDeleting) {
      onDelete();
    }
  }, [isDeleting, onDelete]);

  const handleTouchRow = React.useCallback(() => {
    if (!isOpenRef.current) {
      return;
    }

    setSwipeOpen(false);
    translateX.value = withSpring(0, SWIPE_SPRING);
  }, [setSwipeOpen, translateX]);

  const panGesture = React.useMemo(
    () =>
      Gesture.Pan()
        .activeOffsetX([-SWIPE_ACTIVE_OFFSET, SWIPE_ACTIVE_OFFSET])
        .failOffsetY([-SWIPE_VERTICAL_FAIL_OFFSET, SWIPE_VERTICAL_FAIL_OFFSET])
        .onBegin(() => {
          runOnJS(setTabSwipeBlocked)(true);
          gestureStartX.value = translateX.value;
        })
        .onUpdate((event) => {
          const width = rowWidth.value;
          const nextX = gestureStartX.value + event.translationX;
          translateX.value = Math.max(-width, Math.min(0, nextX));
        })
        .onEnd((event) => {
          const width = rowWidth.value;
          const distance = -translateX.value;
          const dismissDistance = Math.min(
            width * SWIPE_DISMISS_DISTANCE_RATIO,
            SWIPE_DISMISS_MAX_DISTANCE
          );

          if (
            width > 0 &&
            (distance >= dismissDistance ||
              (distance >= SWIPE_NOTIFICATION_ACTION_WIDTH * 0.7 && event.velocityX < -450))
          ) {
            if (isDeleting) {
              runOnJS(setSwipeOpen)(false);
              translateX.value = withSpring(0, SWIPE_SPRING);
              return;
            }

            runOnJS(setSwipeOpen)(false);
            runOnJS(handleDelete)();
            translateX.value = withSpring(-width, SWIPE_SPRING);
            return;
          }

          if (event.translationX > SWIPE_CLOSE_DISTANCE || event.velocityX > 250) {
            runOnJS(setSwipeOpen)(false);
            translateX.value = withSpring(0, SWIPE_SPRING);
            return;
          }

          if (distance >= SWIPE_NOTIFICATION_ACTION_WIDTH * 0.34) {
            runOnJS(setSwipeOpen)(true);
            translateX.value = withSpring(-SWIPE_NOTIFICATION_ACTION_WIDTH, SWIPE_SPRING);
            return;
          }

          runOnJS(setSwipeOpen)(false);
          translateX.value = withSpring(0, SWIPE_SPRING);
        })
        .onFinalize(() => {
          runOnJS(setTabSwipeBlocked)(false);
        }),
    [
      gestureStartX,
      handleDelete,
      isDeleting,
      rowWidth,
      setSwipeOpen,
      setTabSwipeBlocked,
      translateX,
    ]
  );

  const closeTapGesture = React.useMemo(
    () =>
      Gesture.Tap().onEnd(() => {
        runOnJS(handleTouchRow)();
      }),
    [handleTouchRow]
  );

  const gesture = React.useMemo(
    () => Gesture.Simultaneous(panGesture, closeTapGesture),
    [closeTapGesture, panGesture]
  );

  const rowStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const highlightStyle = useAnimatedStyle(() => ({
    opacity: Math.min(0.7, Math.max(0, -translateX.value / SWIPE_NOTIFICATION_ACTION_WIDTH)),
  }));

  const actionStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, Math.max(0, -translateX.value / SWIPE_NOTIFICATION_ACTION_WIDTH)),
  }));

  const handlePressDelete = React.useCallback(() => {
    if (isDeleting) {
      return;
    }

    setSwipeOpen(false);
    handleDelete();
    translateX.value = withSpring(-rowWidth.value, SWIPE_SPRING);
  }, [handleDelete, isDeleting, rowWidth, setSwipeOpen, translateX]);

  const handlePressMore = React.useCallback(() => {
    onMore();
    setSwipeOpen(false);
    translateX.value = withSpring(0, SWIPE_SPRING);
  }, [onMore, setSwipeOpen, translateX]);

  React.useEffect(() => {
    setSwipeOpen(false);
    translateX.value = withSpring(0, SWIPE_SPRING);
  }, [setSwipeOpen, swipeResetKey, translateX]);

  return (
    <View
      className="overflow-hidden"
      style={WEB_SWIPE_ROW_STYLE}
      onLayout={(event) => {
        rowWidth.value = event.nativeEvent.layout.width;
      }}>
      <Reanimated.View
        pointerEvents="none"
        className="absolute inset-0 bg-surface-alt/60"
        style={highlightStyle}
      />
      <GestureDetector gesture={gesture}>
        <Reanimated.View
          className="z-10"
          style={rowStyle}>
          <NotificationRow item={item} emphasized={emphasized} disabled={isOpen} onOpen={onOpen} />
        </Reanimated.View>
      </GestureDetector>
      <Reanimated.View
        pointerEvents="auto"
        style={[
          actionStyle,
          SWIPE_ACTION_CONTAINER_STYLE,
          { gap: 8, width: SWIPE_NOTIFICATION_ACTION_WIDTH },
        ]}>
        <PulseSwipeAction
          accessibilityLabel="More alert options"
          accessibilityHint="Shows notification preference and feedback options"
          icon={MoreHorizontalIcon}
          iconClassName="text-muted-foreground"
          className="bg-surface-alt"
          onPress={handlePressMore}
        />
        <PulseSwipeAction
          accessibilityLabel="Dismiss alert"
          accessibilityHint="Removes this alert from Pulse"
          disabled={isDeleting}
          icon={Trash2Icon}
          iconClassName="text-destructive"
          className="bg-destructive/12"
          onPress={handlePressDelete}
        />
      </Reanimated.View>
    </View>
  );
}

function PulseSwipeAction({
  accessibilityLabel,
  accessibilityHint,
  className,
  disabled = false,
  icon,
  iconClassName,
  onPress,
}: {
  accessibilityLabel: string;
  accessibilityHint: string;
  className: string;
  disabled?: boolean;
  icon: LucideIcon;
  iconClassName: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityHint={accessibilityHint}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      disabled={disabled}
      onPress={onPress}
      className={cn(
        'size-11 items-center justify-center rounded-full border border-border/50 active:opacity-80',
        disabled && 'opacity-50',
        className
      )}>
      <Icon as={icon} size={18} className={iconClassName} />
    </Pressable>
  );
}

function NotificationRow({
  disabled = false,
  item,
  emphasized,
  onOpen,
}: {
  disabled?: boolean;
  item: NotificationItem;
  emphasized: boolean;
  onOpen: () => void;
}) {
  const tone = notificationTone(item.type);
  const headline = notificationHeadline(item);
  const supportingText = notificationSupportingText(item);
  const previewURL = notificationPreviewURL(item);

  return (
    <Pressable
      accessibilityLabel={headline}
      accessibilityRole="button"
      className={cn('px-5', emphasized && 'mx-5 border-l-2 border-primary/35 pl-4')}
      disabled={disabled}
      onPress={onOpen}>
      <View className={cn('flex-row items-stretch gap-3 py-3.5', emphasized && 'py-3')}>
        <NotificationIdentity item={item} tone={tone} />

        <View className="min-w-0 flex-1 gap-1 justify-center">
          <View className="flex-row items-start gap-2">
            <Text
              className="min-w-0 flex-1 text-[14px] font-medium leading-5 text-foreground"
              numberOfLines={2}>
              {headline}
            </Text>

            <View className="ml-auto flex-row items-center pt-0.5">
              <Text variant="small" className="shrink-0 text-[12px] text-muted-foreground/75">
                {formatCompactRelativeTime(item.created_at)}
              </Text>
            </View>
          </View>

          {supportingText ? (
            <Text variant="muted" className="text-muted-foreground/65 leading-5" numberOfLines={2}>
              {supportingText}
            </Text>
          ) : null}
        </View>
        {previewURL ? (
          <Image
            source={{ uri: previewURL }}
            contentFit="cover"
            className="mt-0.5 size-10 rounded-lg bg-muted/80"
          />
        ) : null}
      </View>
    </Pressable>
  );
}

function ActivityRow({ disabled = false, item }: { disabled?: boolean; item: ActivityItem }) {
  const tone = activityTone(item.type);
  const actorHandle = item.actor_username ? `@${item.actor_username}` : null;

  function handlePress() {
    if (item.event_id) {
      router.navigate(getEventHref(item.event_id) as never);
      return;
    }

    if (item.actor_username) {
      router.navigate(getProfileHref(item.actor_username));
    }
  }

  return (
    <Pressable
      accessibilityLabel={item.title}
      accessibilityRole="button"
      className="px-5"
      disabled={disabled}
      onPress={handlePress}>
      <View className="flex-row items-stretch gap-3 py-3">
        {actorHandle ? (
          <ActorAvatar label={actorHandle} className={activityAvatarClassName(item.type)} />
        ) : (
          <View
            className={cn(
              'mt-0.5 size-9 items-center justify-center rounded-2xl',
              tone.iconWrapClassName
            )}>
            <Icon as={tone.Icon} size={16} className={tone.iconClassName} />
          </View>
        )}

        <View className="min-w-0 flex-1 gap-1 justify-center">
          <View className="flex-row items-start gap-2">
            <Text
              className="min-w-0 flex-1 text-[16px] font-semibold leading-6 text-foreground/90"
              numberOfLines={2}>
              {item.title}
            </Text>

            <Text variant="small" className="shrink-0 pt-0.5 text-[12px] text-muted-foreground/75">
              {formatCompactRelativeTime(item.occurred_at)}
            </Text>
          </View>

          {actorHandle ? (
            <View className="flex-row flex-wrap items-center gap-2">
              <Text variant="small" className="text-[13px] text-muted-foreground/70">
                {actorHandle}
              </Text>
            </View>
          ) : null}

          {item.body ? (
            <Text
              variant="small"
              className="text-muted-foreground/65 leading-5"
              numberOfLines={1}>
              {item.body}
            </Text>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
}

function getPulseError(notificationError: unknown, activityError: unknown) {
  if (notificationError instanceof Error) {
    return notificationError.message;
  }

  if (activityError instanceof Error) {
    return activityError.message;
  }

  if (notificationError || activityError) {
    return 'Unable to load pulse';
  }

  return null;
}

function toFriendlyPulseError(error: string | null) {
  if (!error) {
    return {
      title: "Couldn't load pulse.",
      body: 'Please try again.',
    };
  }

  const normalized = error.toLowerCase();
  if (normalized.includes('unable to reach api at') || normalized.includes('network request failed')) {
    return {
      title: "Can't connect right now.",
      body: "We couldn't reach Radious. Check your connection and try again.",
    };
  }

  return {
    title: "Couldn't load pulse.",
    body: error,
  };
}

function buildActivitySections(items: ActivityItem[]): ActivitySection[] {
  const buckets: Record<ActivitySection['key'], ActivityItem[]> = {
    comment: [],
    rsvp: [],
    host: [],
  };

  for (const item of items) {
    if (item.type === 'comment' || item.type === 'rsvp' || item.type === 'host') {
      buckets[item.type].push(item);
    }
  }

  const sections: ActivitySection[] = [
    {
      key: 'comment',
      title: 'Comments',
      data: buckets.comment,
    },
    {
      key: 'rsvp',
      title: 'People Going',
      data: buckets.rsvp,
    },
    {
      key: 'host',
      title: 'From People You Follow',
      data: buckets.host,
    },
  ];

  return sections.filter((section) => section.data.length > 0);
}

function friendActivityKey(item: FriendActivityItem) {
  return [
    item.type,
    item.actor.user_id,
    item.event?.id ?? item.target_user?.user_id ?? 'none',
    item.created_at,
  ].join(':');
}

function friendActivityActionText(item: FriendActivityItem, targetName: string) {
  switch (item.type) {
    case 'friend_rsvp':
      return ` is going to ${item.event?.title ?? 'an event'}`;
    case 'friend_save':
      return ` saved ${item.event?.title ?? 'an event'}`;
    case 'friend_follow':
      return ` and ${targetName} are now friends`;
    default:
      return ' has new activity';
  }
}

function friendActivityMetaText(item: FriendActivityItem) {
  const timeText = formatCompactRelativeTime(item.created_at);
  if (item.event?.venue_name) {
    return `${item.event.venue_name} · ${timeText}`;
  }
  return timeText;
}

function friendActivityAccessibilityLabel(
  item: FriendActivityItem,
  actorName: string,
  targetName: string
) {
  switch (item.type) {
    case 'friend_rsvp':
      return `${actorName} is going to ${item.event?.title ?? 'an event'}`;
    case 'friend_save':
      return `${actorName} saved ${item.event?.title ?? 'an event'}`;
    case 'friend_follow':
      return `${actorName} and ${targetName} are now friends`;
    default:
      return `${actorName} has new activity`;
  }
}

function activityTone(type: ActivityItem['type']): ActivityTone {
  switch (type) {
    case 'comment':
      return ACTIVITY_TONES.comment;
    case 'rsvp':
      return ACTIVITY_TONES.rsvp;
    case 'host':
      return ACTIVITY_TONES.host;
    default:
  return ACTIVITY_TONES.host;
  }
}

function notificationTone(type: NotificationType): NotificationTone {
  return NOTIFICATION_TONES[type] ?? DEFAULT_NOTIFICATION_TONE;
}

function getNotificationSettingsCategory(
  type: NotificationType
): NotificationSettingsCategory | null {
  switch (type) {
    case 'friend_rsvp':
    case 'follow_added':
      return 'social';
    case 'going_event_starting_soon':
    case 'saved_event_starting_soon':
      return 'reminders';
    case 'venue_new_event':
    case 'venue_event_cancelled':
    case 'venue_live_deal':
    case 'venue_announcement':
    case 'event_boost':
      return 'host_updates';
    case 'weekly_digest':
      return 'weekly_digest';
    case 'event_cancelled':
    default:
      return null;
  }
}

function activityAvatarClassName(type: ActivityItem['type']) {
  switch (type) {
    case 'comment':
      return 'bg-primary/10';
    case 'rsvp':
      return 'bg-primary/10';
    case 'host':
      return 'bg-surface-alt';
    default:
      return 'bg-surface-alt';
  }
}

function withAlpha(color: string, alpha: number) {
  if (color.startsWith('#') && color.length === 7) {
    const r = Number.parseInt(color.slice(1, 3), 16);
    const g = Number.parseInt(color.slice(3, 5), 16);
    const b = Number.parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  return color;
}

function notificationHeadline(item: NotificationItem) {
  switch (item.type) {
    case 'follow_added':
      return item.payload.username ? `@${item.payload.username} followed you` : item.payload.copy;
    case 'friend_rsvp':
    case 'going_event_starting_soon':
    case 'saved_event_starting_soon':
    case 'venue_new_event':
    case 'venue_event_cancelled':
    case 'venue_live_deal':
      return item.payload.copy || item.payload.title || DEFAULT_NOTIFICATION_TONE.label;
    case 'venue_announcement':
      return item.payload.title || item.payload.copy || DEFAULT_NOTIFICATION_TONE.label;
    case 'weekly_digest':
    default:
      return item.payload.title || item.payload.copy || DEFAULT_NOTIFICATION_TONE.label;
  }
}

function notificationSupportingText(item: NotificationItem) {
  switch (item.type) {
    case 'follow_added':
    case 'friend_rsvp':
    case 'going_event_starting_soon':
    case 'saved_event_starting_soon':
    case 'venue_new_event':
    case 'venue_event_cancelled':
    case 'venue_live_deal':
      return null;
    case 'venue_announcement':
      return item.payload.copy && item.payload.copy !== item.payload.title ? item.payload.copy : null;
    default:
      return item.payload.copy && item.payload.copy !== item.payload.title ? item.payload.copy : null;
  }
}

function formatCompactRelativeTime(value: string) {
  const elapsedMinutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));

  if (!Number.isFinite(elapsedMinutes)) {
    return formatRelativeTime(value);
  }

  if (elapsedMinutes < 1) {
    return 'now';
  }

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes} min`;
  }

  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours} hr`;
  }

  const elapsedDays = Math.round(elapsedHours / 24);
  return `${elapsedDays} d`;
}

function ActorAvatar({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <Avatar alt={label} className={cn('size-9 border border-border/70', className)}>
      <AvatarFallback className="bg-transparent">
        <Text variant="small" className="pt-1 font-semibold leading-none text-foreground">
          {getProfileInitials(label.replace(/^@/, ''))}
        </Text>
      </AvatarFallback>
    </Avatar>
  );
}

function NotificationIdentity({
  item,
  tone,
}: {
  item: NotificationItem;
  tone: ReturnType<typeof notificationTone>;
}) {
  const actorLabel = item.payload.display_name?.trim() || item.payload.username?.trim() || '';
  const avatarURL = item.payload.avatar_url?.trim() ?? '';

  if (actorLabel.length > 0) {
    return (
      <Avatar alt={actorLabel} className="mt-0.5 size-8 border border-border bg-surface-alt">
        {avatarURL.length > 0 ? <AvatarImage source={{ uri: avatarURL }} className="h-full w-full" /> : null}
        <AvatarFallback className="bg-transparent">
          <Text className="text-[11px] font-semibold leading-none text-foreground">
            {getProfileInitials(actorLabel.replace(/^@/, ''))}
          </Text>
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <View
      className="mt-0.5 size-8 items-center justify-center rounded-2xl"
      style={{ backgroundColor: tone.iconBackgroundColor }}>
      <Icon as={tone.Icon} size={16} color={tone.iconColor} />
    </View>
  );
}

function notificationPreviewURL(item: NotificationItem) {
  const imageURL = item.payload.image_url?.trim() ?? '';
  return imageURL.length > 0 ? resolveEventPosterURL(imageURL) : null;
}
